import {useIntl} from 'react-intl';
import React, {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {OverlayTrigger, Popover, Tooltip} from 'react-bootstrap';
import TableMetronic from '../../../common/components/table/TableMetronic';
import ModalAddUser from '../ModalAddUser';
import ModalEditUser from '../ModalEditUser';
import ModalDeleteUser from '../ModalDeleteUser';
import ModalEditUserRights from '../ModalEditUserRights';
import CisContact from '../../../classes/api/CisContact';
import Role from '../../../classes/api/Role';
import {useImpersonate} from '../../../hooks/useGetMe';
import SecuredComponent from '../../../modules/auth/authorization/SecuredComponent';
import {usePermissions} from '../../../modules/auth/authorization/PermissionProvider';
import {checkIfAuthorized} from '../../../modules/auth/authorization/api';
import {Customer} from '../../../../Domains/Auth/Types/customer';
import {compareRoleNames} from '../RoleSwitchList';
import {RoleUser, User} from '../../../../Domains/Auth/Types/User';

const SUPER_ADMIN_ROLE = 'Super Administrateur';

export const userMatchesSearch = (user: User, term: string): boolean =>
  `${user.first_name ?? ''} ${user.name ?? ''} ${user.mail ?? ''}`.toLowerCase().includes(term);

// un utilisateur est sur la BU s'il y est rattaché ou s'il y a un rôle
export const isUserOnBu = (user: User, buId: number): boolean =>
  parseInt(String(user.entity_id)) === buId ||
  user.role_user.some((roleUser) => parseInt(String(roleUser.entity_id)) === buId);

// utilisateurs présents sur la BU, dédoublonnés par email
export const getUsersOnBu = (users: User[], buId: number): User[] =>
  users
    .filter((user) => isUserOnBu(user, buId))
    .filter((user, index, onBu) => onBu.findIndex((other) => other.mail === user.mail) === index);

export const userHasRole = (user: User, buId: number, roleIds: number[]): boolean =>
  roleIds.length === 0 ||
  user.role_user.some(
    (roleUser) =>
      parseInt(String(roleUser.entity_id)) === buId && roleIds.includes(roleUser.role.id)
  );

interface UserManagerV2Props {
  customer: Customer;
  customers: Customer[];
  users: User[];
  cisContacts: CisContact[];
  roles: Role[];
  userSearch?: string;
  roleIds?: number[];
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const getInitials = (user: User): string =>
  `${user.first_name?.charAt(0) ?? ''}${user.name?.charAt(0) ?? ''}`.toUpperCase() || '?';

interface IconButtonProps {
  icon: string;
  label: string;
  variant?: 'primary' | 'danger';
  onClick: () => void;
}

const IconButton = ({icon, label, variant = 'primary', onClick}: IconButtonProps): JSX.Element => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-${label}`}>{label}</Tooltip>}>
    <button
      type="button"
      className={`btn btn-icon btn-sm btn-light-${variant}`}
      aria-label={label}
      onClick={onClick}
    >
      <i className={`fa-solid ${icon}`}></i>
    </button>
  </OverlayTrigger>
);

const UserManagerV2 = ({
  customer,
  customers,
  users,
  cisContacts,
  roles,
  userSearch = '',
  roleIds = [],
}: UserManagerV2Props): JSX.Element => {
  const intl = useIntl();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRightEditModal, setShowRightEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();

  const {mutate: mutateImpersonate} = useImpersonate();
  const isAdmin = checkIfAuthorized(usePermissions(), ['ecAdmin']);

  const usersOnBu = useMemo<User[]>(() => getUsersOnBu(users, customer.id), [customer.id, users]);

  const displayedUsers = useMemo<User[]>(
    () =>
      usersOnBu.filter(
        (user) =>
          (!userSearch || userMatchesSearch(user, userSearch)) &&
          userHasRole(user, customer.id, roleIds)
      ),
    [usersOnBu, userSearch, roleIds, customer.id]
  );

  const usersNotInBu = useMemo<User[]>(() => {
    const result: User[] = [];
    for (const user of users) {
      if (
        !result.some((u) => u.mail === user.mail) &&
        !usersOnBu.some((u) => u.mail === user.mail)
      ) {
        result.push(user);
      }
    }
    for (const cisContact of cisContacts) {
      if (!result.some((u) => u.mail === cisContact.getMail())) {
        result.push({
          id: cisContact.getId(),
          name: cisContact.getLastName() ?? '',
          first_name: cisContact.getFirstName() ?? '',
          mail: cisContact.getMail() ?? '',
          entity_id: customer.id,
          role_user: [],
        });
      }
    }
    return result
      .filter((user) => user.mail !== null)
      .sort((a, b) => a.first_name!.localeCompare(b.first_name!));
  }, [cisContacts, users, usersOnBu, customer.id]);

  const selectedUserAccounts = useMemo<Customer[]>(() => {
    if (!selectedUser) {
      return [];
    }

    const accounts = customers.filter(
      (account) =>
        account.id === customer.id ||
        users.some(
          (user) =>
            user.mail === selectedUser.mail && parseInt(String(user.entity_id)) === account.id
        ) ||
        selectedUser.role_user.some(
          (roleUser) => parseInt(String(roleUser.entity_id)) === account.id
        )
    );

    if (isAdmin) {
      const knownIds = new Set([customer.id, ...accounts.map((account) => account.id)]);

      const addAccount = (entityId: number, name: string | null): void => {
        if (!knownIds.has(entityId)) {
          knownIds.add(entityId);
          accounts.push({id: entityId, name: name ?? '', system: customer.system});
        }
      };

      selectedUser.role_user.forEach((roleUser) =>
        addAccount(parseInt(String(roleUser.entity_id)), roleUser.business_name)
      );
      users
        .filter((user) => user.mail === selectedUser.mail)
        .forEach((user) => addAccount(parseInt(String(user.entity_id)), null));
    }

    return [customer, ...accounts.filter((account) => account.id !== customer.id)];
  }, [selectedUser, customers, customer, users, isAdmin]);

  const openRights = useCallback((user: User) => {
    setSelectedUser(user);
    setShowRightEditModal(true);
  }, []);
  const openEdit = useCallback((user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  }, []);
  const openDelete = useCallback((user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  }, []);

  const renderRoleBadge = useCallback(
    (roleUser: RoleUser): JSX.Element =>
      roleUser.role.name === SUPER_ADMIN_ROLE ? (
        <OverlayTrigger
          key={roleUser.role.id}
          placement="top"
          overlay={
            <Tooltip id={`role-former-name-${roleUser.role.id}`}>
              {intl.formatMessage({id: 'USER_MANAGER.ROLE.SUPER_ADMINISTRATEUR.FORMER_NAME'})}
            </Tooltip>
          }
        >
          <span className="badge badge-light-primary fs-7" style={{textTransform: 'none'}}>
            <i className="fa-solid fa-user-tie me-1"></i>
            {capitalize(roleUser.role.name)}
          </span>
        </OverlayTrigger>
      ) : (
        <span
          key={roleUser.role.id}
          className="badge badge-light-primary fs-7"
          style={{textTransform: 'none'}}
        >
          {capitalize(roleUser.role.name)}
        </span>
      ),
    [intl]
  );

  const renderRoleBadges = useCallback(
    (user: User): JSX.Element => {
      const rolesOnBu = user.role_user
        // eslint-disable-next-line
        .filter((roleUser) => roleUser.entity_id == customer.id)
        .sort((a, b) => compareRoleNames(a.role.name, b.role.name));
      if (rolesOnBu.length === 0) {
        return <span className="text-gray-400">—</span>;
      }
      const [firstRole, ...otherRoles] = rolesOnBu;
      return (
        <div className="d-flex flex-wrap align-items-center gap-2">
          {renderRoleBadge(firstRole)}
          {otherRoles.length > 0 && (
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="bottom"
              overlay={
                <Popover id={`other-roles-${user.id}`}>
                  <Popover.Body className="d-flex flex-column align-items-start gap-2 p-3">
                    {otherRoles.map(renderRoleBadge)}
                  </Popover.Body>
                </Popover>
              }
            >
              <button
                type="button"
                className="btn btn-sm btn-outline btn-active-light-primary border"
              >
                {intl.formatMessage(
                  {
                    id: 'USER_MANAGER.TABLE.MORE_ROLES',
                    defaultMessage: '+{count} {count, plural, one {rôle} other {rôles}}',
                  },
                  {count: otherRoles.length}
                )}
              </button>
            </OverlayTrigger>
          )}
        </div>
      );
    },
    [customer.id, intl, renderRoleBadge]
  );

  const renderIdentity = useCallback(
    (user: User): JSX.Element => (
      <div className="d-flex align-items-center">
        <div className="symbol symbol-circle symbol-40px me-3 flex-shrink-0">
          <div className="symbol-label bg-light-primary text-primary fw-bold">
            {getInitials(user)}
          </div>
        </div>
        <div className="d-flex flex-column" style={{minWidth: 0}}>
          <span className="fw-bold text-gray-800">
            {user.first_name} {user.name}
          </span>
          <span className="text-muted fs-7" style={{wordBreak: 'break-word'}}>
            {user.mail}
          </span>
        </div>
      </div>
    ),
    []
  );

  const renderActions = useCallback(
    (user: User): JSX.Element => (
      <div className="d-flex align-items-center gap-2">
        <IconButton
          icon="fa-bars"
          label={intl.formatMessage({id: 'USER_MANAGER.TOOLTIP.SET_RIGHTS'})}
          onClick={() => openRights(user)}
        />
        <IconButton
          icon="fa-user-gear"
          label={intl.formatMessage({id: 'USER_MANAGER.TOOLTIP.EDIT_ACCOUNT'})}
          onClick={() => openEdit(user)}
        />
        <SecuredComponent requiredPermissions={['ecAdmin']}>
          <IconButton
            icon="fa-person-walking-arrow-right"
            label={intl.formatMessage({
              id: 'USER_MANAGER.TOOLTIP.IMPERSONATE',
              defaultMessage: 'Se connecter en tant que',
            })}
            onClick={() => mutateImpersonate(user)}
          />
        </SecuredComponent>
        <span className="border-start border-gray-300 align-self-stretch mx-1"></span>
        <IconButton
          icon="fa-user-slash"
          variant="danger"
          label={intl.formatMessage({id: 'USER_MANAGER.TOOLTIP.DELETE_ACCOUNT'})}
          onClick={() => openDelete(user)}
        />
      </div>
    ),
    [intl, openRights, openEdit, openDelete, mutateImpersonate]
  );

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'USER_MANAGER.TABLE.COLUMN.USER'}),
        id: 'user',
        accessorFn: (user) => `${user.first_name ?? ''} ${user.name ?? ''} ${user.mail ?? ''}`,
        cell: (info) => renderIdentity(info.row.original),
      },
      {
        header: intl.formatMessage({id: 'USER_MANAGER.TABLE.COLUMN.ROLES'}),
        id: 'roles',
        enableSorting: false,
        cell: (info) => renderRoleBadges(info.row.original),
      },
      {
        header: intl.formatMessage({id: 'USER_MANAGER.TABLE.COLUMN.ACTIONS'}),
        id: 'actions',
        enableSorting: false,
        cell: (info) => renderActions(info.row.original),
      },
    ],
    [intl, renderIdentity, renderRoleBadges, renderActions]
  );

  const renderMobileCard = (user: User): JSX.Element => (
    <div className="d-flex flex-column gap-3">
      {renderIdentity(user)}
      {renderRoleBadges(user)}
      {renderActions(user)}
    </div>
  );

  const addUserLabel = intl.formatMessage({id: 'USER_MANAGER.TABLE.ADD_USER'});

  return (
    <>
      <TableMetronic<User>
        data={displayedUsers}
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={false}
        pagination={true}
        searchBar={true}
        noRowsMessage={intl.formatMessage({id: 'USERS.NOT.FOUND'})}
        toolbar={
          <button
            type="button"
            className="btn btn-primary btn-celeste-primary"
            title={addUserLabel}
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fa fa-plus-circle fs-4 me-2"></i>
            <span className="d-none d-md-inline">{addUserLabel}</span>
          </button>
        }
      />
      {selectedUser && (
        <>
          <ModalEditUser
            showModal={showEditModal}
            setShowModal={setShowEditModal}
            user={selectedUser}
            setSelectedUser={setSelectedUser}
          />
          <ModalEditUserRights
            showModal={showRightEditModal}
            setShowModal={setShowRightEditModal}
            user={selectedUser}
            roles={roles}
            customer={customer}
          />
          <ModalDeleteUser
            showModal={showDeleteModal}
            setShowModal={setShowDeleteModal}
            user={selectedUser}
            accounts={selectedUserAccounts}
            customer={customer}
          />
        </>
      )}
      <ModalAddUser
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        customer={customer}
        users={usersNotInBu}
        roles={roles}
      />
    </>
  );
};

export default UserManagerV2;
